import { ArrowLeft, ArrowRight, CircleStop, EyeOff, MonitorPlay } from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useEscapeLayer } from '../../components/ui/escape';
import { Portal } from '../../components/ui/layer';
import { cn } from '../../lib/cn';
import { useIsPhone, useReducedMotion } from '../../lib/hooks';
import { ensureVisible, findTarget, hasOpenModal, isRendered } from './dom';
import type { TourPlacement, TourStep } from './tours';

export type ActiveTour = {
  /** Номер запуску: повторний запуск того самого тура починає його з першого кроку. */
  key: number;
  id: string;
  title: string;
  steps: TourStep[];
  /** Демонстрація: кроки перемикаються самі. */
  demo: boolean;
  /** Що зробити, коли цілі кроку немає на екрані. */
  missingHint?: string;
};

export type TourEndReason = 'done' | 'skip' | 'escape';

/** Тривалість кроку в демонстрації. */
export const DEMO_STEP_MS = 4500;
/** Скільки чекати на появу цілі (лінива сторінка, завантаження даних), перш ніж показати примітку. */
const SEARCH_GRACE_MS = 1200;
/** Період звірки положення цілі (зсуви макета без прокручування й зміни розміру). */
const TRACK_MS = 300;
const MOVE_MS = 180;
const RING_PAD = 4;
const GAP = 12;
const EDGE = 12;
const PHONE_EDGE = 16;

type Box = { top: number; left: number; width: number; height: number };
type Side = 'top' | 'bottom' | 'left' | 'right';
type Status = 'searching' | 'found' | 'missing';

const SIDES: Side[] = ['bottom', 'top', 'right', 'left'];

function clamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), Math.max(min, max));
}

function sameBox(a: Box | null, b: Box | null) {
  if (!a || !b) return a === b;
  return Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
}

/** Рамка навколо цілі з невеликим відступом, але в межах вікна (інакше межу не видно на елементах на всю ширину). */
function ringBox(r: Box, vw: number, vh: number): Box {
  const left = Math.max(2, r.left - RING_PAD);
  const top = Math.max(2, r.top - RING_PAD);
  const right = Math.min(vw - 2, r.left + r.width + RING_PAD);
  const bottom = Math.min(vh - 2, r.top + r.height + RING_PAD);
  return { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/**
 * Положення картки: поруч із ціллю з бажаного боку, інакше з іншого, де вміщується;
 * якщо ціль велика — у кут, найдальший від її центру. На телефоні — на всю ширину зверху або знизу,
 * з протилежного від цілі боку екрана.
 */
function placeCard(
  target: Box | null,
  size: { w: number; h: number },
  pref: TourPlacement | undefined,
  keep: Side | null,
  vp: { vw: number; vh: number; safeTop: number; safeBottom: number },
  phone: boolean,
): { top: number; left: number; side: Side | null } {
  const { vw, vh, safeTop, safeBottom } = vp;
  if (phone) {
    const low = vh - size.h - PHONE_EDGE - safeBottom;
    const high = PHONE_EDGE + safeTop;
    if (!target) return { top: Math.max(high, low), left: PHONE_EDGE, side: null };
    const lowerHalf = target.top + target.height / 2 > vh / 2;
    return { top: lowerHalf ? high : Math.max(high, low), left: PHONE_EDGE, side: null };
  }
  if (!target) return { top: Math.max(EDGE, (vh - size.h) / 2), left: Math.max(EDGE, (vw - size.w) / 2), side: null };
  const order: Side[] = [];
  for (const s of [keep, pref && pref !== 'auto' ? pref : null, ...SIDES]) if (s && !order.includes(s)) order.push(s);
  const cx = target.left + target.width / 2;
  const cy = target.top + target.height / 2;
  const x = (v: number) => clamp(v, EDGE, vw - EDGE - size.w);
  const y = (v: number) => clamp(v, EDGE, vh - EDGE - size.h);
  for (const side of order) {
    if (side === 'bottom' && target.top + target.height + GAP + size.h <= vh - EDGE) return { top: target.top + target.height + GAP, left: x(cx - size.w / 2), side };
    if (side === 'top' && target.top - GAP - size.h >= EDGE) return { top: target.top - GAP - size.h, left: x(cx - size.w / 2), side };
    if (side === 'right' && target.left + target.width + GAP + size.w <= vw - EDGE) return { top: y(cy - size.h / 2), left: target.left + target.width + GAP, side };
    if (side === 'left' && target.left - GAP - size.w >= EDGE) return { top: y(cy - size.h / 2), left: target.left - GAP - size.w, side };
  }
  return { top: cy > vh / 2 ? EDGE : vh - EDGE - size.h, left: cx > vw / 2 ? EDGE : vw - EDGE - size.w, side: null };
}

function readViewport(probe: HTMLElement | null) {
  let safeTop = 0;
  let safeBottom = 0;
  if (probe) {
    const cs = getComputedStyle(probe);
    safeTop = parseFloat(cs.paddingTop) || 0;
    safeBottom = parseFloat(cs.paddingBottom) || 0;
  }
  return { vw: window.innerWidth, vh: window.innerHeight, safeTop, safeBottom };
}

type Props = {
  tour: ActiveTour;
  onClose: (reason: TourEndReason) => void;
  /** Куди повернути фокус після закриття (елемент, з якого запустили тур). */
  returnFocus: HTMLElement | null;
};

/**
 * Шар тура: затемнення з «вікном» над ціллю й рамкою, картка кроку (не модальна для решти сторінки),
 * клавіші ← → і Esc, демонстрація з автоматичним перемиканням кроків.
 *
 * Якщо цілі кроку немає на екрані, картка показується по центру з приміткою, що зробити,
 * і прив’язується до цілі, щойно та з’явиться (крок не пропускається — пояснення лишається корисним).
 */
export function TourLayer({ tour, onClose, returnFocus }: Props) {
  const steps = tour.steps;
  const total = steps.length;
  const [index, setIndex] = useState(0);
  const step = steps[Math.min(index, total - 1)]!;
  const last = index >= total - 1;
  const reduced = useReducedMotion();
  const phone = useIsPhone();

  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const sideRef = useRef<Side | null>(null);
  const movedAtRef = useRef(0);
  const focusWasInsideRef = useRef(false);

  const [status, setStatus] = useState<Status>('searching');
  const [rect, setRect] = useState<Box | null>(null);
  const [viewport, setViewport] = useState(() => readViewport(null));
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  const [cardTick, setCardTick] = useState(0);
  const [announce, setAnnounce] = useState('');

  const [playing, setPlaying] = useState(tour.demo);
  const [hover, setHover] = useState(false);
  const [kbdFocus, setKbdFocus] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const paused = hover || kbdFocus || modalOpen || status === 'searching';
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const titleId = useId();
  const bodyId = useId();

  const goTo = useCallback(
    (i: number) => {
      focusWasInsideRef.current = !!cardRef.current?.contains(document.activeElement);
      movedAtRef.current = performance.now();
      setIndex(clamp(i, 0, total - 1));
    },
    [total],
  );
  const next = useCallback(() => {
    if (index >= total - 1) onClose('done');
    else goTo(index + 1);
  }, [index, total, goTo, onClose]);
  const prev = useCallback(() => {
    if (index > 0) goTo(index - 1);
  }, [index, goTo]);

  useEscapeLayer(true, () => onClose('escape'));

  // Стрілки ← → — коли фокус у картці тура або ніде (не заважаємо полям, спискам і вкладкам сторінки).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const t = e.target as Node | null;
      const inCard = !!t && !!cardRef.current?.contains(t);
      const nowhere = t === document.body || t === document.documentElement || t === null;
      if (!inCard && !nowhere) return;
      e.preventDefault();
      if (e.key === 'ArrowRight') next();
      else prev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [next, prev]);

  // Фокус у картку під час запуску (у демонстрації — на саму картку, щоб не ставити показ на паузу),
  // повернення — після закриття, якщо користувач не перейшов деінде.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const target = tour.demo ? cardRef.current : primaryRef.current ?? cardRef.current;
      target?.focus({ preventScroll: true });
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active && active !== document.body && active.isConnected) return;
        if (returnFocus && returnFocus.isConnected && isRendered(returnFocus)) returnFocus.focus({ preventScroll: true });
      }, 0);
    };
    // Лише під час монтування й зняття шару.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Пошук цілі кроку й стеження за її положенням: прокручування (усі контейнери), зміна розміру, зсуви макета.
  useLayoutEffect(() => {
    let alive = true;
    let raf = 0;
    const startedAt = performance.now();
    let scrolledFor: HTMLElement | null = null;
    let observed: Element | null = null;
    targetRef.current = null;
    sideRef.current = null;
    const ro = new ResizeObserver(() => schedule());

    const observe = (el: Element | null) => {
      if (observed === el) return;
      if (observed) ro.unobserve(observed);
      observed = el;
      if (el) ro.observe(el);
    };

    function sync() {
      raf = 0;
      if (!alive) return;
      let el = targetRef.current;
      if (!el || !isRendered(el)) {
        el = findTarget(step.target);
        targetRef.current = el;
        observe(el);
      }
      if (!el) {
        setRect(null);
        setStatus(performance.now() - startedAt < SEARCH_GRACE_MS ? 'searching' : 'missing');
        return;
      }
      if (scrolledFor !== el) {
        scrolledFor = el;
        ensureVisible(el);
      }
      const r = el.getBoundingClientRect();
      const box = { top: r.top, left: r.left, width: r.width, height: r.height };
      setRect((prev) => (sameBox(prev, box) ? prev : box));
      setStatus('found');
    }
    function schedule() {
      if (!raf && alive) raf = requestAnimationFrame(sync);
    }
    const measureViewport = () => {
      const v = readViewport(probeRef.current);
      setViewport((prev) => (prev.vw === v.vw && prev.vh === v.vh && prev.safeTop === v.safeTop && prev.safeBottom === v.safeBottom ? prev : v));
    };
    const onResize = () => {
      measureViewport();
      schedule();
    };

    measureViewport();
    sync();
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', onResize);
    const interval = window.setInterval(schedule, TRACK_MS);
    return () => {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', onResize);
      window.clearInterval(interval);
    };
  }, [index, step.target]);

  // Картка змінює розмір (текст кроку, шрифти) — перераховуємо положення.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCardTick((n) => n + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ring = status === 'found' && rect ? ringBox(rect, viewport.vw, viewport.vh) : null;
  const target = ring && ring.width > 0 && ring.height > 0 ? ring : null;

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const p = placeCard(target, { w: card.offsetWidth, h: card.offsetHeight }, step.placement, sideRef.current, viewport, phone);
    if (p.side) sideRef.current = p.side;
    setCardPos((prev) => (prev && Math.abs(prev.top - p.top) < 0.5 && Math.abs(prev.left - p.left) < 0.5 ? prev : { top: p.top, left: p.left }));
    // target — похідне від rect/status/viewport; перелічуємо первинні значення.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect, status, viewport, phone, cardTick, index, step.placement]);

  // Після перемикання кроку: якщо фокус був у картці й загубився (кнопку сховано) — на основну кнопку.
  useLayoutEffect(() => {
    if (!focusWasInsideRef.current) return;
    focusWasInsideRef.current = false;
    if (!cardRef.current?.contains(document.activeElement)) primaryRef.current?.focus({ preventScroll: true });
  }, [index]);

  // Оголошення кроку для зчитувачів екрана (перший крок оголошує сам діалог під час фокусування).
  const announcedIndexRef = useRef(index);
  useEffect(() => {
    if (announcedIndexRef.current === index) return;
    announcedIndexRef.current = index;
    setAnnounce(`Крок ${index + 1} з ${total}. ${step.title}. ${step.body}`);
  }, [index, total, step.title, step.body]);

  // Демонстрація: смуга прогресу й таймер кроку — одна анімація (пауза зупиняє обидва).
  const animRef = useRef<Animation | null>(null);
  useEffect(() => {
    if (!playing) return;
    const bar = barRef.current;
    let anim: Animation | null = null;
    let fallback = 0;
    const done = () => {
      if (index >= total - 1) setPlaying(false);
      else goTo(index + 1);
    };
    if (bar && typeof bar.animate === 'function') {
      anim = bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: DEMO_STEP_MS, easing: 'linear', fill: 'forwards' });
      if (pausedRef.current) anim.pause();
      anim.onfinish = done;
      animRef.current = anim;
    } else {
      fallback = window.setTimeout(done, DEMO_STEP_MS);
    }
    return () => {
      if (anim) {
        anim.onfinish = null;
        anim.cancel();
      }
      animRef.current = null;
      window.clearTimeout(fallback);
    };
  }, [playing, index, total, goTo]);

  useEffect(() => {
    const a = animRef.current;
    if (!a) return;
    if (paused) a.pause();
    else if (a.playState === 'paused') a.play();
  }, [paused]);

  // Поверх відкрито модальний діалог — шар тура під ним інертний, демонстрацію ставимо на паузу.
  useEffect(() => {
    if (!playing) {
      setModalOpen(false);
      return;
    }
    const check = () => setModalOpen(hasOpenModal());
    check();
    const t = window.setInterval(check, 500);
    return () => window.clearInterval(t);
  }, [playing]);

  const animate = !reduced && performance.now() - movedAtRef.current < MOVE_MS + 120;
  const moveTransition = `top ${MOVE_MS}ms var(--ease-out), left ${MOVE_MS}ms var(--ease-out), width ${MOVE_MS}ms var(--ease-out), height ${MOVE_MS}ms var(--ease-out)`;
  const hole: Box = target ?? { top: viewport.vh / 2, left: viewport.vw / 2, width: 0, height: 0 };
  const holeStyle: CSSProperties = {
    top: hole.top,
    left: hole.left,
    width: hole.width,
    height: hole.height,
    boxShadow: '0 0 0 9999px var(--c-overlay)',
    outline: target ? '2px solid var(--c-accent)' : 'none',
    outlineOffset: 0,
    transition: animate ? `opacity 150ms var(--ease-out), ${moveTransition}` : 'opacity 150ms var(--ease-out)',
  };
  const cardStyle: CSSProperties = {
    top: cardPos?.top ?? 0,
    left: cardPos?.left ?? 0,
    transition: animate ? `top ${MOVE_MS}ms var(--ease-out), left ${MOVE_MS}ms var(--ease-out)` : undefined,
  };

  return (
    <Portal>
      <div aria-hidden ref={probeRef} className="pointer-events-none invisible fixed top-0 left-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]" />
      <div data-training-layer="" aria-hidden className="pointer-events-none fixed z-[46] rounded-md starting:opacity-0" style={holeStyle} />
      <div
        ref={cardRef}
        data-training-layer=""
        role="dialog"
        aria-modal="false"
        aria-label={`Навчальний тур: ${tour.title}`}
        aria-describedby={`${titleId} ${bodyId}`}
        tabIndex={-1}
        data-ers-layer=""
        data-keep-panel=""
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onFocus={(e) => {
          const t = e.target as HTMLElement;
          if (t !== cardRef.current && t.matches(':focus-visible')) setKbdFocus(true);
        }}
        onBlur={(e) => {
          if (!cardRef.current?.contains(e.relatedTarget as Node | null)) setKbdFocus(false);
        }}
        className={cn(
          'ers-pop fixed z-[60] flex max-h-[calc(100dvh-24px)] flex-col overflow-hidden rounded-lg border border-line bg-raised text-fg shadow-pop',
          phone ? 'w-[calc(100vw-32px)]' : 'w-[min(360px,calc(100vw-24px))]',
          !cardPos && 'invisible',
        )}
        style={cardStyle}
      >
        <div className="flex items-center gap-2 px-4 pt-3">
          <span className="min-w-0 flex-1 truncate text-caption text-fg-3">{tour.title}</span>
          <span className="tnum shrink-0 text-caption font-medium text-fg-2">
            Крок {index + 1} з {total}
          </span>
        </div>
        <div aria-hidden className="mt-2 flex gap-1 px-4">
          {steps.map((s, i) => (
            <span key={s.id} className={cn('h-0.5 flex-1 rounded-full', i <= index ? 'bg-fg-2' : 'bg-surface-3')} />
          ))}
        </div>

        <div className="min-h-0 overflow-y-auto px-4 pt-3">
          <h2 id={titleId} className="text-body font-semibold text-fg">
            {step.title}
          </h2>
          <p id={bodyId} className="mt-1 text-meta text-fg-2">
            {step.body}
          </p>
          {status === 'missing' && (
            <p className="mt-2 flex items-start gap-1.5 rounded-sm bg-surface-2 px-2 py-1.5 text-caption text-fg-2">
              <EyeOff className="mt-px size-3.5 shrink-0 text-fg-3" aria-hidden />
              <span>
                Цього елемента зараз немає на екрані. {tour.missingHint ?? 'Перейдіть до відповідного розділу — підказка прив’яжеться до нього.'}
              </span>
            </p>
          )}
        </div>

        {tour.demo && playing && (
          <div className="mt-3 px-4">
            <div className="flex items-center gap-2 text-caption text-fg-3">
              <MonitorPlay className="size-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">{paused ? 'Демонстрацію призупинено' : 'Демонстрація: кроки перемикаються самі'}</span>
              <Button
                variant="ghost"
                size="sm"
                className="-mr-2"
                icon={<CircleStop className="size-3.5" aria-hidden />}
                onClick={() => {
                  setPlaying(false);
                  // Кнопка зникає разом із рядком демонстрації — фокус переходить на «Далі».
                  window.setTimeout(() => primaryRef.current?.focus({ preventScroll: true }), 0);
                }}
              >
                Зупинити
              </Button>
            </div>
            <span aria-hidden className="mt-1 block h-0.5 overflow-hidden rounded-full bg-surface-3 motion-reduce:invisible">
              {/* Початковий стан — inline transform: утиліта scale-x-* задає властивість scale й перемножилася б з анімацією. */}
              <span ref={barRef} className="block h-full origin-left bg-fg-3" style={{ transform: 'scaleX(0)' }} />
            </span>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 border-t border-line px-3 py-2">
          {!last && (
            <Button variant="ghost" size="sm" onClick={() => onClose('skip')}>
              Пропустити
            </Button>
          )}
          <span className="ml-auto flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              aria-disabled={index === 0 || undefined}
              icon={<ArrowLeft className="size-3.5" aria-hidden />}
              onClick={prev}
              className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              Назад
            </Button>
            <Button ref={primaryRef} variant="primary" size="sm" iconRight={last ? undefined : <ArrowRight className="size-3.5" aria-hidden />} onClick={next}>
              {last ? 'Готово' : 'Далі'}
            </Button>
          </span>
        </div>
        <p className="sr-only" aria-live="polite">
          {announce}
        </p>
      </div>
    </Portal>
  );
}
